import { Check } from 'lucide-react';

interface Step {
  id: string;
  label: string;
}

interface WizardStepsProps {
  steps: Step[];
  currentStep: number;
  completedSteps: Set<number>;
  progressLabel: string;
}

export function WizardSteps({
  steps,
  currentStep,
  completedSteps,
  progressLabel,
}: WizardStepsProps) {
  return (
    <div className="relative mb-4 sm:mb-6">
      <div
        className="mb-3 flex items-center justify-between text-xs font-semibold text-text-muted-light dark:text-text-muted-dark sm:hidden"
        aria-live="polite"
      >
        <span>{steps[currentStep]?.label}</span>
        <span>
          {currentStep + 1} / {steps.length}
        </span>
      </div>
      {/* Progress bar background */}
      <div
        className="absolute top-5 left-0 right-0 h-0.5 bg-surface-border-light dark:bg-surface-border-dark -z-0"
        aria-hidden="true"
      />

      {/* Active progress bar */}
      <div
        className="absolute top-5 left-0 h-0.5 bg-brand-600 transition-all duration-500 ease-out -z-0"
        aria-hidden="true"
        style={{
          width: steps.length > 1 ? `${(currentStep / (steps.length - 1)) * 100}%` : '0%',
        }}
      />

      <div
        className="flex items-start justify-between relative z-10"
        role="list"
        aria-label={progressLabel}
      >
        {steps.map((step, index) => {
          const isCompleted = completedSteps.has(index);
          const isCurrent = index === currentStep;

          return (
            <div
              key={step.id}
              role="listitem"
              aria-label={`${step.label} ${index + 1} / ${steps.length}`}
              aria-current={isCurrent ? 'step' : undefined}
              className="flex flex-col items-center"
              style={{ width: `${100 / steps.length}%` }}
            >
              {/* Step circle */}
              <div
                aria-hidden="true"
                className={`
                  w-10 h-10 rounded-full flex items-center justify-center border-2 font-black text-sm
                  transition-all duration-300 shadow-sm
                  ${
                    isCompleted
                      ? 'bg-brand-600 border-brand-600 text-white shadow-brand-200 dark:shadow-none'
                      : isCurrent
                        ? 'bg-surface-card-light border-brand-600 text-brand-600 dark:bg-surface-2-dark dark:border-brand-400 dark:text-brand-400 shadow-md ring-4 ring-accent-text-light/20 dark:ring-accent-text-dark/20'
                        : 'bg-surface-card-light border-border-strong-light text-text-subtle-light dark:bg-surface-2-dark dark:border-border-strong-dark dark:text-text-subtle-dark'
                  }
                `}
              >
                {isCompleted ? (
                  <Check size={18} strokeWidth={3} />
                ) : (
                  <span className={isCurrent ? 'scale-110' : ''}>{index + 1}</span>
                )}
              </div>

              {/* Step label */}
              <p
                className={`
                  hidden sm:block mt-2 text-[9px] font-black uppercase tracking-wide text-center leading-tight whitespace-nowrap
                  transition-colors duration-300
                  ${
                    isCurrent
                      ? 'text-brand-600 dark:text-brand-400'
                      : isCompleted
                        ? 'text-slate-700 dark:text-slate-300'
                        : 'text-text-subtle-light dark:text-text-subtle-dark'
                  }
                `}
              >
                {step.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
